'use client';

import { useState } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { Input } from '@/components/ui/forms/input';
import { Label } from '@/components/ui/forms/label';
import { Textarea } from '@/components/ui/forms/textarea';
import { Button } from '@/components/ui/forms/button';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { generateId } from '../../utils';
import ImproveTextControl from '../ImproveTextControl';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/layout/collapsible';
import { WithCommentIndicator } from '@/components/features/resume/AnalysisCommentIndicator';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

interface CustomSectionsSectionProps {
  guestMode?: boolean;
  onGuestAction?: () => void;
  /** Analysis suggestions targeting each custom section's content, keyed by section id. */
  commentSuggestionMap?: Map<string, ResumeAnalysisSuggestion[]>;
  onApplyCommentSuggestion?: (suggestion: ResumeAnalysisSuggestion) => void;
  applyingSuggestionId?: string | null;
}

export default function CustomSectionsSection({
  guestMode,
  onGuestAction,
  commentSuggestionMap,
  onApplyCommentSuggestion,
  applyingSuggestionId,
}: CustomSectionsSectionProps) {
  const { resume, addCustomSection, updateCustomSection, removeCustomSection } =
    useResumeBuilderStore();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  if (!resume) return null;

  const { customSections } = resume.content;

  const handleAdd = () => {
    const id = generateId();
    const maxOrder = customSections.reduce((max, s) => Math.max(max, s.order), 0);
    addCustomSection({
      id,
      title: '',
      content: '',
      order: maxOrder + 1,
    });
    setExpandedIds((prev) => new Set([...prev, id]));
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort by order
  const sortedSections = [...customSections].sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-4">
      <p className="text-body text-muted-foreground">
        Add custom sections for additional information like projects, publications, volunteer work,
        or languages.
      </p>

      {sortedSections.length > 0 && (
        <div className="space-y-3">
          {sortedSections.map((section, index) => (
            <Collapsible
              key={`${section.id}-${index}`}
              open={expandedIds.has(section.id)}
              onOpenChange={() => toggleExpanded(section.id)}
            >
              <div className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div className="flex-1">
                      <div className="text-label">{section.title || 'New Section'}</div>
                    </div>
                    {expandedIds.has(section.id) ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-border p-4">
                    <div className="space-y-2">
                      <Label>Section Title *</Label>
                      <Input
                        value={section.title}
                        onChange={(e) => updateCustomSection(section.id, { title: e.target.value })}
                        placeholder="Projects, Publications, Languages, etc."
                      />
                    </div>

                    <WithCommentIndicator
                      suggestions={commentSuggestionMap?.get(section.id) ?? []}
                      onApply={onApplyCommentSuggestion ?? (() => {})}
                      isApplying={applyingSuggestionId}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Content</Label>
                          <ImproveTextControl
                            text={section.content || ''}
                            field="custom"
                            instructionPlaceholder="e.g., Make it more concise, add more detail..."
                            onImproved={(improvedText) =>
                              updateCustomSection(section.id, { content: improvedText })
                            }
                            guestMode={guestMode}
                            onGuestAction={onGuestAction}
                          />
                        </div>
                        <Textarea
                          value={section.content}
                          onChange={(e) =>
                            updateCustomSection(section.id, { content: e.target.value })
                          }
                          placeholder="Add your content here..."
                          rows={6}
                        />
                        <p className="text-caption text-muted-foreground">
                          Put each item on its own line to render it as a bulleted list. Leave a blank
                          line between paragraphs to keep them as prose.
                        </p>

                      </div>
                    </WithCommentIndicator>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeCustomSection(section.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" onClick={handleAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Add Custom Section
      </Button>
    </div>
  );
}
